import { useCallback, useState } from 'react';
import Swal from 'sweetalert2';
import Cookies from 'universal-cookie';
import { apiUrl } from '../config/api';

const cookies = new Cookies();

/**
 * "Add to cart" behaviour shared by every product listing.
 *
 * Previously this function was copy-pasted into Home, HotProducts,
 * NewArrival, AlsoLike, Search and Productshow. Behaviour (endpoint,
 * payload, common_id handling, guest fallback to the login modal) is
 * unchanged; it additionally tracks a pending id so the button that was
 * clicked can show a loading state and cannot be double-submitted.
 */
const useCartActions = ({ onCartOpen, onLoginRequired } = {}) => {
  const [pendingVariantId, setPendingVariantId] = useState(null);

  const addToCart = useCallback(
    async (variantId, productCode, quantity = 1) => {
      if (variantId == null) {
        Swal.fire({
          icon: 'warning',
          confirmButtonColor: '#b63f23',
          title: 'Select Variant!',
        });
        return;
      }

      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('user_id') || '';
      const commonId = localStorage.getItem('common_id') || '';

      setPendingVariantId(variantId);
      try {
        const response = await fetch(apiUrl('/api/cart/add'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            common_id: commonId,
            user_id: parseInt(userId, 10),
            product_code: productCode,
            quantity: parseInt(quantity, 10) || 1,
            variant_id: variantId,
          }),
        });

        if (!response.ok) throw new Error('Network response was not ok');

        const data = await response.json();

        if (data.cart?.common_id) {
          localStorage.setItem('common_id', data.cart.common_id);
        }

        if (data.full) {
          Swal.fire({
            icon: 'info',
            confirmButtonColor: '#b63f23',
            title: 'Maximum available quantity reached',
          });
        }

        onCartOpen?.();
      } catch (error) {
        if (token || userId) {
          Swal.fire({
            icon: 'error',
            confirmButtonColor: '#b63f23',
            title: 'Could not add to cart',
            text: 'Please check your connection and try again.',
          });
        } else {
          // Session is gone — clear it and ask the visitor to sign in.
          localStorage.clear();
          cookies.remove('user_id');
          onLoginRequired?.();
        }
      } finally {
        setPendingVariantId(null);
      }
    },
    [onCartOpen, onLoginRequired]
  );

  return { addToCart, pendingVariantId };
};

export default useCartActions;
